import {
    Draggable,
    type LatLng,
    LatLngBounds,
    type LeafletMouseEvent,
} from "leaflet";
import {
    type ReactNode,
    use,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { SVGOverlay } from "react-leaflet";
import { useSelector } from "react-redux";

import {
    markerColors,
    markerIcons,
    markerRadius,
    selectedMarkerColor,
} from "@/constants/marker-constants";
import type { MovePoint } from "@/hooks";
import type { MarkerPosition } from "@/models/marker-position";
import type {
    DraggableLayer,
    DraggableMap,
    LeafletDraggable,
} from "@/types/map-types";

import { FixtureSelectionContext } from "../fixture-selection-provider";

/**
 * Props for the {@link Marker} component.
 */
interface MarkerProps {
    /**
     * The marker position and type to render.
     */
    model: MarkerPosition;
    /**
     * Called when the marker is clicked.
     */
    onSelect?: (evt: LeafletMouseEvent) => void;
    /**
     * Called with the new position after a drag completes.
     */
    onMoveEnd: (points: MovePoint[]) => void;
    /**
     * Optional function to snap a point to the nearest grid position.
     * When provided, applied during drag-end so the marker visually
     * lands on the snapped position immediately.
     */
    snapPoint?: (point: LatLng) => LatLng;
}

/**
 * Renders a non-luminaire device marker on the floor map as a single
 * SVGOverlay containing both the colored ring and the device icon. The
 * overlay's bounds are in map units so the marker scales with zoom (the
 * same way `Light`'s Rectangle scales). Dragging is handled directly on
 * the overlay's DOM element via `L.Draggable`, so the ring and icon
 * always move as one unit with no inter-element sync.
 *
 * @param {MarkerProps} props - The marker position model, selection handler, and move handler.
 * @returns {ReactNode} The rendered marker, or null while mounting.
 */
const Marker = ({
    model,
    onSelect,
    onMoveEnd,
    snapPoint,
}: MarkerProps): ReactNode => {
    const overlayRef = useRef(null);
    const [isMounted, setMounted] = useState(false);
    const selection = use(FixtureSelectionContext);

    const isSelected = useSelector(({ map }) =>
        map.general.selectedMarkers.includes(model.uniqueId),
    );

    const { fill, stroke } = isSelected
        ? selectedMarkerColor
        : markerColors[model.markerType];

    const icon = markerIcons[model.markerType];

    const bounds = useMemo(
        () =>
            new LatLngBounds(
                [model.y - markerRadius, model.x - markerRadius],
                [model.y + markerRadius, model.x + markerRadius],
            ),
        [model.y, model.x],
    );

    const handleClick = useCallback(
        (evt: LeafletMouseEvent) => {
            evt.originalEvent?.stopPropagation();

            const map = evt?.target?._map as DraggableMap | undefined;
            if (map?.moved) {
                map.moved = false;
                return;
            }

            onSelect?.(evt);
        },
        [onSelect],
    );

    useEffect(() => {
        if (!overlayRef.current || !isMounted) return () => {};
        return selection?.add(model.uniqueId, overlayRef.current);
    }, [model.uniqueId, selection, isMounted]);

    useEffect(() => {
        if (!isMounted || !overlayRef.current) return;

        const overlay = overlayRef.current as DraggableLayer<SVGElement>;
        const el = overlay.getElement();

        if (!el || !isSelected) return;

        const draggable = new Draggable(
            el as unknown as HTMLElement,
        ) as unknown as LeafletDraggable;

        draggable.enable();

        const map = overlay._map;

        const handleDragStart = () => {
            map.movingLights = true;
        };

        const handleDragEnd = () => {
            const dx = draggable._newPos.x - draggable._startPos.x;
            const dy = draggable._newPos.y - draggable._startPos.y;

            const startLayerPoint = map.latLngToLayerPoint({
                lat: model.y,
                lng: model.x,
            });

            const newLatLng = map.layerPointToLatLng([
                startLayerPoint.x + dx,
                startLayerPoint.y + dy,
            ]);

            const finalLatLng = snapPoint ? snapPoint(newLatLng) : newLatLng;

            const finalBounds = new LatLngBounds(
                [
                    finalLatLng.lat - markerRadius,
                    finalLatLng.lng - markerRadius,
                ],
                [
                    finalLatLng.lat + markerRadius,
                    finalLatLng.lng + markerRadius,
                ],
            );

            overlay.setBounds(finalBounds);

            const dest = [
                {
                    id: model.uniqueId,
                    point: finalLatLng,
                },
            ];

            onMoveEnd(dest);

            map.moved = true;

            requestAnimationFrame(() => {
                map.moved = false;
                map.movingLights = false;
            });
        };

        draggable.on("dragstart", handleDragStart);
        draggable.on("dragend", handleDragEnd);

        return () => {
            draggable.disable();
        };
    }, [
        isSelected,
        isMounted,
        model.uniqueId,
        model.x,
        model.y,
        onMoveEnd,
        snapPoint,
    ]);

    useEffect(() => {
        // Workaround to get the layers to render correctly
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setMounted(true);
    }, []);

    if (!isMounted) return null;

    return (
        <SVGOverlay
            ref={overlayRef}
            bounds={bounds}
            interactive
            attributes={{ viewBox: "0 0 100 100" }}
            eventHandlers={{ click: handleClick }}
        >
            <defs>
                <filter id={`marker-shadow-${model.id}`}>
                    <feDropShadow
                        dx="0"
                        dy="2"
                        stdDeviation="3"
                        floodOpacity="0.3"
                    />
                </filter>
            </defs>
            <g transform={`rotate(${model.angle} 50 50)`}>
                <circle
                    cx="50"
                    cy="50"
                    r="48"
                    fill={fill}
                    stroke={stroke}
                    strokeWidth="2"
                    filter={`url(#marker-shadow-${model.id})`}
                />
                <image
                    href={icon}
                    x="10"
                    y="10"
                    width="80"
                    height="80"
                    preserveAspectRatio="xMidYMid meet"
                />
            </g>
        </SVGOverlay>
    );
};

export default Marker;
